import React, { FC, useEffect, useState } from 'react';
import { Button, ButtonStyles, Popup, PopupStyles, TextInput } from '@silverfort/sf-components';
import { PolicyTemplate } from '../../../../types/policies';

interface Props {
    open: boolean;
    onClose: () => void;
    template?: PolicyTemplate;
    onDuplicateClick: (templateId: PolicyTemplate['id'], templateName: string) => void;
}

interface ContentProps {
    name: string;
    onNameChange: (name: string) => void;
}

interface FooterProps {
    duplicateDisabled: boolean;
    onCancelClick: () => void;
    onDuplicateClick: () => void;
}

const Content: FC<ContentProps> = ({ name, onNameChange }) => {
    return (
        <div className="flex flex-col gap-4 w-full pb-3">
            <span className="font-sf text-13 leading-[19px] tracking-[0.0156rem]">
                Select name for the new template
            </span>
            <TextInput
                className="w-full font-medium leading-[19px] tracking-[0.0156rem]"
                value={name}
                onChange={onNameChange}
            />
        </div>
    );
};

const Footer: FC<FooterProps> = ({ duplicateDisabled, onCancelClick, onDuplicateClick }) => (
    <div className="gap-4 flex justify-end items-center">
        <Button buttonStyle={ButtonStyles.GhostSolid} text="Cancel" onClick={onCancelClick} />
        <Button disabled={duplicateDisabled} text="Duplicate" onClick={onDuplicateClick} />
    </div>
);

const getDefaultName = (template?: PolicyTemplate): string => {
    return template ? `${template.name}(copy)` : '';
};

const DuplicateTemplateModal: FC<Props> = ({ open, template, onClose, onDuplicateClick }) => {
    const [name, setName] = useState(getDefaultName(template));

    useEffect(() => {
        setName(getDefaultName(template));
    }, [open, template]);

    const handleDuplicateClick = () => {
        if (template) {
            onDuplicateClick(template.id, name);
        }
    };

    return (
        <Popup
            open={open}
            style={PopupStyles.Confirmation}
            title="Duplicate template"
            popupClassName="w-[360px]"
            onClose={onClose}
            ContentComponent={Content}
            FooterComponent={Footer}
            ContentComponentProps={{ name, onNameChange: setName }}
            FooterComponentProps={{
                duplicateDisabled: !name,
                onDuplicateClick: handleDuplicateClick,
                onCancelClick: onClose
            }}
        />
    );
};

export default DuplicateTemplateModal;
